import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize token from localStorage synchronously to prevent redirect on refresh
  const getInitialToken = () => {
    try {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        // Set axios header immediately if token exists
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        return storedToken;
      }
    } catch (error) {
      console.error('Error reading token from localStorage:', error);
    }
    return null;
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getInitialToken());
  const expirationCheckIntervalRef = useRef(null);

  // Function to check if token is expired (simple JWT decode)
  const isTokenExpired = (tokenToCheck) => {
    if (!tokenToCheck) return true;
    try {
      const base64Url = tokenToCheck.split('.')[1];
      if (!base64Url) return true;
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      const currentTime = Date.now() / 1000;
      
      return decoded.exp && decoded.exp < currentTime;
    } catch (error) {
      return true; // If we can't decode, consider expired
    }
  };

  // Function to handle logout
  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear expiration check interval
    if (expirationCheckIntervalRef.current) {
      clearInterval(expirationCheckIntervalRef.current);
      expirationCheckIntervalRef.current = null;
    }
  };

  // Setup request interceptor to mark login requests
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Mark public auth/owner requests so response interceptor can skip them
        if (config.url) {
          const skip401Paths = ['/auth/login', '/public/owners'];
          if (skip401Paths.some(path => config.url.includes(path))) {
            config._skip401Handler = true;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Setup response interceptor for 401 handling
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only handle 401 if it's NOT a login request
        if (error.response?.status === 401) {
          // Check if this is a login request (marked by request interceptor)
          const isLoginRequest = error.config?._skip401Handler || 
                                (error.config?.url && error.config.url.includes('/auth/login'));
          
          if (!isLoginRequest) {
            console.warn('401 Unauthorized - Token expired or invalid. Logging out...');
            handleLogout();
            
            // Redirect to login if not already there
            if (window.location.pathname !== '/sy-admin/login') {
              setTimeout(() => {
                window.location.href = '/sy-admin/login';
              }, 100);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Initialize token on mount - validate token and set user
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    
    if (storedToken) {
      // Validate token expiration
      if (!isTokenExpired(storedToken)) {
        // Token exists and is valid
        setToken(storedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        setUser({ username: 'admin' });
      } else {
        // Token exists but is expired, clear it
        console.warn('Stored token is expired. Clearing...');
        handleLogout();
      }
    } else {
      // No token, ensure clean state
      setToken(null);
      setUser(null);
    }
    
    setLoading(false);
  }, []);

  // Update token and headers when token changes, and setup periodic checks
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Clear any existing interval
      if (expirationCheckIntervalRef.current) {
        clearInterval(expirationCheckIntervalRef.current);
      }
      
      // Setup periodic expiration check (every 5 minutes)
      expirationCheckIntervalRef.current = setInterval(() => {
        const currentToken = localStorage.getItem('token');
        
        if (!currentToken || isTokenExpired(currentToken)) {
          console.warn('Token expired during session. Logging out...');
          handleLogout();
          
          // Redirect to login if not already there
          if (window.location.pathname !== '/sy-admin/login') {
            window.location.href = '/sy-admin/login';
          }
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      // Clear interval when token is removed
      if (expirationCheckIntervalRef.current) {
        clearInterval(expirationCheckIntervalRef.current);
        expirationCheckIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (expirationCheckIntervalRef.current) {
        clearInterval(expirationCheckIntervalRef.current);
        expirationCheckIntervalRef.current = null;
      }
    };
  }, [token]);

  const login = async (username, password) => {
    try {
      console.log('Attempting login to:', `${API_BASE_URL}/auth/login`);
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      });

      console.log('Login response:', response.data);

      if (response.data.success) {
        const newToken = response.data.token;
        // Set token immediately in axios headers before state update
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        localStorage.setItem('token', newToken);
        // Then update state (which will trigger useEffect)
        setToken(newToken);
        setUser(response.data.admin);
        return { success: true };
      } else {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      // Handle network errors
      if (!error.response) {
        return {
          success: false,
          message: 'Cannot connect to server. Please make sure the backend is running.'
        };
      }
      
      // Handle 401 specifically for login (invalid credentials)
      if (error.response?.status === 401) {
        return {
          success: false,
          message: error.response?.data?.message || 'Invalid username or password'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    handleLogout();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

